import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GearSix } from '@phosphor-icons/react';
import TaskTemplatesEditor from '@/components/tarefas/TaskTemplatesEditor';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function TaskSettingsPage() {
  const [programs, setPrograms] = useState([]);
  const [renewal, setRenewal] = useState(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/task-settings`);
      setPrograms(data.programs || []);
      setRenewal(data.renewal_sessions_before_end ?? 2);
    } catch { toast.error('Erro ao carregar configurações'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateProgram = (key, field, value) => {
    setPrograms(ps => ps.map(p => p.key === key ? { ...p, [field]: value } : p));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/task-settings`, { programs, renewal_sessions_before_end: renewal });
      toast.success('Configurações guardadas');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-brand-placeholder">A carregar…</div>;

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="task-settings-page">
      <h1 className="font-heading text-2xl text-brand-text mb-1 flex items-center gap-2"><GearSix size={24} /> Tarefas Automáticas</h1>
      <p className="text-sm text-brand-muted mb-6">Define os limiares que disparam cada tarefa automática, por programa.</p>

      <div className="bg-white border border-brand-border rounded-2xl overflow-hidden mb-6">
        <div className="grid grid-cols-3 gap-2 px-4 py-2.5 bg-brand-bg text-[11px] font-medium text-brand-muted">
          <span>Programa</span>
          <span>Dias p/ agendar consulta</span>
          <span>Semanas s/ atualizar plano</span>
        </div>
        {programs.map(p => (
          <div key={p.key} className="grid grid-cols-3 gap-2 px-4 py-2.5 border-t border-brand-border/50 items-center" data-testid={`program-row-${p.key}`}>
            <span className="text-sm text-brand-text">{p.label}</span>
            <Input type="number" min={1} value={p.days_next_appointment} onChange={e => updateProgram(p.key, 'days_next_appointment', parseInt(e.target.value) || 1)} className="h-9 text-sm" data-testid={`days-input-${p.key}`} />
            <Input type="number" min={1} value={p.weeks_meal_plan_update} onChange={e => updateProgram(p.key, 'weeks_meal_plan_update', parseInt(e.target.value) || 1)} className="h-9 text-sm" data-testid={`weeks-input-${p.key}`} />
          </div>
        ))}
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-brand-text">Renovação de programa</p>
          <p className="text-xs text-brand-muted">Sessões antes do fim que disparam a tarefa (todos os programas)</p>
        </div>
        <Input type="number" min={0} value={renewal} onChange={e => setRenewal(parseInt(e.target.value) || 0)} className="h-9 text-sm w-20 text-center" data-testid="renewal-sessions-input" />
      </div>

      <Button onClick={save} disabled={saving} className="bg-brand-green text-white" data-testid="save-task-settings-btn">{saving ? 'A guardar…' : 'Guardar'}</Button>

      <TaskTemplatesEditor />
    </div>
  );
}
